import React, { useState } from 'react';
import { axiosKK } from '../../services/networkRequest';
import errorMessage from '../../services/errorMessage';
import Button from '../Button';
import { AdminNotice, SectionHead } from './Shared';

/** `POST /admin/hubspot/import-cancellations` — a preview unless `dryRun=false`. */
export interface HubspotCancellationImportResult {
  dryRun: boolean;
  dealsCancelledInHubspot: number;
  matchedOrders: number;
  alreadyCancelled: number;
  orderIdsToCancel: number[];
  orderIdsCancelled: number[];
  dealsWithoutStorageOrder: number;
}

const ENDPOINT = '/admin/hubspot/import-cancellations';

/**
 * Brings in the orders already marked "Order cancelled" in HubSpot. Changes made
 * from now on arrive through the webhook; this is for the ones made before it
 * listened. A cancelled order leaves the working lists and is skipped by the
 * automatic final bill, so the admin sees the list before anything changes.
 */
export default function HubspotCancellationImport() {
  const [preview, setPreview] = useState<HubspotCancellationImportResult>();
  const [done, setDone] = useState<HubspotCancellationImportResult>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const run = (dryRun: boolean) => {
    setBusy(true);
    setError(undefined);
    axiosKK
      .post<HubspotCancellationImportResult>(ENDPOINT, undefined, { params: { dryRun } })
      .then(({ data }) => {
        if (dryRun) {
          setPreview(data);
          setDone(undefined);
        } else {
          setDone(data);
          setPreview(undefined);
        }
      })
      .catch((err) => errorMessage(setError)(err))
      .finally(() => setBusy(false));
  };

  return (
    <section className="admin-dashboard-orders" aria-label="HubSpot cancellations">
      <SectionHead
        title="HubSpot cancellations"
        caption="Mark the orders that are already cancelled in HubSpot as cancelled here. You see the list first; nothing changes until you confirm."
        actions={
          <Button type="button" variant="secondary" size="sm" onClick={() => run(true)} disabled={busy}>
            {busy && !preview ? 'Checking…' : 'Check HubSpot'}
          </Button>
        }
      />
      <AdminNotice intent="error">{error}</AdminNotice>

      {preview && (
        <div className="admin-hubspot-import">
          <p>
            {preview.dealsCancelledInHubspot} deals are cancelled in HubSpot. {preview.matchedOrders} of
            them are orders here, of which {preview.alreadyCancelled} are already cancelled
            {preview.dealsWithoutStorageOrder > 0 &&
              `, and ${preview.dealsWithoutStorageOrder} have no matching order`}
            .
          </p>
          {preview.orderIdsToCancel.length === 0 ? (
            <AdminNotice intent="info">Nothing to import: every one is already cancelled here.</AdminNotice>
          ) : (
            <>
              <p>
                {preview.orderIdsToCancel.length} orders will be marked cancelled:{' '}
                {preview.orderIdsToCancel.map((id) => `#${id}`).join(', ')}
              </p>
              <Button type="button" variant="primary" size="sm" onClick={() => run(false)} disabled={busy}>
                {busy ? 'Importing…' : `Mark ${preview.orderIdsToCancel.length} orders cancelled`}
              </Button>
            </>
          )}
        </div>
      )}

      {done && (
        <AdminNotice intent="success">
          {done.orderIdsCancelled.length} orders marked cancelled
          {done.orderIdsCancelled.length < done.orderIdsToCancel.length &&
            ` (${done.orderIdsToCancel.length - done.orderIdsCancelled.length} had changed in the meantime and were left alone)`}
          .
        </AdminNotice>
      )}
    </section>
  );
}
